import { produce } from 'immer';

import { ValueEditorType } from '../../../types/basic';
import { DefaultOperator } from '../../operator-selector';
import { ValueEditorProps } from '../ValueEditor';

type Value = undefined | string | string[] | null;

export interface UseValueEditorReturnProps {
  baseProps: {
    className: string;
    placeholder?: string;
  };
  valueEditorType: ValueEditorType;
  handleSingleValue: (value: Value) => void;
  handleMultipleValues: (value: Value, index?: number) => void;
  getErrorMessage: (i?: number) => string | undefined;
  compact?: boolean;
}

export interface ValueEditorInputProps
  extends Omit<UseValueEditorReturnProps, 'baseProps' | 'valueEditorType' | 'getErrorMessage'> {
  field: ValueEditorProps['field'];
  values: ValueEditorProps['values'];
}

export const useValueEditor = ({
  field,
  values = [],
  operator,
  errors,
  onChange,
  compact,
}: ValueEditorProps): UseValueEditorReturnProps => {
  const valueEditorType =
    (typeof field.valueEditorType === 'function'
      ? field.valueEditorType(operator)
      : field.valueEditorType) || 'text';

  const baseProps = {
    className: compact ? 'w-full' : 'w-full max-w-60',
    placeholder: field.placeholder,
  };

  const handleSingleValue = (value?: string | string[] | null) => {
    if (typeof value === 'undefined' || value === null) return;

    if (Array.isArray(value)) {
      onChange?.(value);
    } else {
      onChange?.([value]);
    }
  };

  const handleMultipleValues = (value?: string | string[] | null, index?: number) => {
    if (typeof value !== 'string' || typeof index === 'undefined') return;

    const producedVal = produce<string[]>(values, (val) => {
      val[index] = value;
      if (
        index === 0 &&
        (operator === DefaultOperator.enum.BETWEEN ||
          operator === DefaultOperator.enum.NOT_BETWEEN) &&
        !val[1]
      ) {
        val[1] = '';
      }
    });

    onChange?.(producedVal);
  };

  const getErrorMessage = (i = 0) => {
    return errors?.[i];
  };

  return {
    baseProps,
    valueEditorType,
    handleSingleValue,
    handleMultipleValues,
    getErrorMessage,
  };
};
